import * as React from "react";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import cn from "classnames";

import {
    Dialog,
    DialogContent,
    Button,
    AlertDialog,
    AlertDialogContent,
    Card,
    ActionButton,
} from "@jhool-io/fe-components";
import { useFetchInvoiceNotes } from "../../../../hooks/queries/billing";
import styles from "./InvoiceNotes.module.scss";
import ListState from "../../../../components/ListState/ListState";
import useToast from "../../../../hooks/useToast";
import {
    displayNameInRightFormat,
    formatDate,
} from "../../../../utils/helpers";
import InvoiceNotesList from "./InvoiceNotesList/InvoiceNotesList";
import { ViewEditorText } from "../../../../components/TextEditor/ViewEditorText/ViewEditorText";
import {
    IInvoiceNoteDocument,
    IInvoiceNotesResponse,
} from "../../../../utils/types/billing";
import { useDeleteInvoiceNote } from "../../hooks/billing.mutations";
import PlusIcon from "../../../../icons/Plus";
import Loader from "../../../../shared-ui/Loader/Loader";
import DeleteIcon from "../../../../icons/Delete";
import EditIcon from "../../../../icons/Edit";
import AddInvoiceNoteForm from "../AddInvoiceNoteForm/AddInvoiceNoteForm";
import ViewInvoiceNoteDoc from "../ViewInvoiceNoteDoc/ViewInvoiceNoteDoc";
import EditInvoiceNoteForm from "../EditInvoiceNoteForm/EditInvoiceNoteForm";

interface InvoiceNotesProps {
    invoiceId: string;
    isNoShowNote?: boolean;
}

type Dialogs = "add" | "edit" | "delete" | "view_all" | "view_doc";

export default function InvoiceNotes({
    invoiceId,
    isNoShowNote,
}: InvoiceNotesProps) {
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    const [selectedInvoiceNoteId, setSelectedInvoiceNoteId] = React.useState<
        string | null
    >(null);
    const [docInView, setDocInView] =
        React.useState<IInvoiceNoteDocument | null>(null);
    const [invoiceNoteToEdit, setInvoiceNoteToEdit] =
        React.useState<IInvoiceNotesResponse | null>(null);

    const deleteInvoiceNote = useDeleteInvoiceNote();

    const isAddInvoiceNoteMutating = useIsMutating(["add-invoice-note"]);
    const isAddInvoiceNoteDocMutating = useIsMutating([
        "add-invoice-note-document",
    ]);
    const editInvoiceNoteMutating = useIsMutating(["edit-invoice-note"]);

    // query client
    const queryClient = useQueryClient();
    const { toast } = useToast();

    // fetch 3 latest invoice notes
    const { data, isLoading, error, isSuccess } = useFetchInvoiceNotes(
        invoiceId,
        {
            invoice_id: invoiceId,
            limit: 3,
        },
        Boolean(invoiceId)
    );

    const listHeader = (
        <div className={styles.header}>
            <h3>Internal notes</h3>

            <div className={styles.btnwrapper}>
                {!error && !(isSuccess && data.data.length === 0) && (
                    <Button
                        variant="normal"
                        size="auto"
                        aria-label="view all internal notes"
                        className="underline text-base text-textLink font-normal mr-3"
                        onClick={() => setDialogInView("view_all")}
                    >
                        view all notes
                    </Button>
                )}

                <ActionButton
                    variant="normal"
                    size="auto"
                    aria-label="add internal note button"
                    className="size-7 flex items-center justify-center rounded-md bg-primary"
                    onClick={() => setDialogInView("add")}
                    actionTitle="Add note"
                >
                    <PlusIcon className="stroke-white" />
                </ActionButton>
            </div>
        </div>
    );

    // isolate the different unique dates
    const recentInternalNotesDates = new Set<string>();
    data?.data.forEach((note) =>
        recentInternalNotesDates.add(formatDate(note.date_created))
    );

    // group recent internal notes by unique dates
    const sortedInternalNotes: { [key: string]: IInvoiceNotesResponse[] } = {};

    recentInternalNotesDates.forEach((date) => {
        const availableNotes: IInvoiceNotesResponse[] = [];
        data?.data.forEach((note) => {
            if (formatDate(note.date_created) === date) {
                availableNotes.push(note);
            }
        });

        sortedInternalNotes[date] = availableNotes;
    });

    const handleDeleteInvoiceNote = () => {
        deleteInvoiceNote.mutate(
            {
                invoiceId,
                invoiceNoteId: selectedInvoiceNoteId ?? "",
            },
            {
                onSuccess: async (res) => {
                    await queryClient.invalidateQueries({
                        queryKey: ["invoice-notes", invoiceId],
                    });
                    toast({
                        mode: "success",
                        message:
                            res.message || "Internal note deleted successfully",
                    });
                    setDialogInView(null);
                    setSelectedInvoiceNoteId(null);
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not delete internal note, please try again",
                    });
                },
            }
        );
    };

    const handleShowEditDialog = (invoiceNote: IInvoiceNotesResponse) => {
        setDialogInView("edit");

        setInvoiceNoteToEdit(invoiceNote);
    };

    const handleCloseEditDialog = () => {
        setDialogInView(null);

        setInvoiceNoteToEdit(null);
    };

    return (
        <>
            {docInView ? (
                <Dialog open={dialogInView === "view_doc"}>
                    <DialogContent
                        title="View Internal note attachement"
                        handleCloseDialog={() => {
                            setDialogInView(null);
                            setDocInView(null);
                        }}
                        showFooter={false}
                    >
                        <ViewInvoiceNoteDoc
                            url={docInView.url}
                            contentType={
                                docInView?.metadata?.content_type ?? ""
                            }
                            title={
                                docInView?.document_name ??
                                "Invoice note attachment"
                            }
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <AlertDialog open={dialogInView === "delete"}>
                <AlertDialogContent
                    onSaveClick={handleDeleteInvoiceNote}
                    saveText="Delete Internal Note"
                    onCancelClick={() => setDialogInView(null)}
                    title="Delete Internal Note"
                    description="Are you sure you want to delete this  internal note?"
                    isSaveBtnDisabled={deleteInvoiceNote.isLoading}
                    isCancelBtnDisabled={deleteInvoiceNote.isLoading}
                    isDeleting
                />
            </AlertDialog>
            {invoiceNoteToEdit ? (
                <Dialog open={dialogInView === "edit"}>
                    <DialogContent
                        title="Edit internal note"
                        handleCloseDialog={handleCloseEditDialog}
                        variant="center"
                        showFooter
                        saveText="Edit internal note"
                        cancelText="Cancel"
                        submitBtnFormValue="edit-invoice-note-form"
                        isSubmitBtnDisabled={
                            editInvoiceNoteMutating > 0 ||
                            isAddInvoiceNoteDocMutating > 0
                        }
                        isCancelBtnDisabled={
                            editInvoiceNoteMutating > 0 ||
                            isAddInvoiceNoteDocMutating > 0
                        }
                    >
                        <EditInvoiceNoteForm
                            invoiceNote={invoiceNoteToEdit}
                            onFormSubmit={handleCloseEditDialog}
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <Dialog open={dialogInView === "add"}>
                <DialogContent
                    title="Add internal note"
                    handleCloseDialog={() => setDialogInView(null)}
                    variant="center"
                    showFooter
                    saveText="Add internal note"
                    cancelText="Cancel"
                    submitBtnFormValue="add-internal-note"
                    isSubmitBtnDisabled={
                        isAddInvoiceNoteMutating > 0 ||
                        isAddInvoiceNoteDocMutating > 0
                    }
                    isCancelBtnDisabled={
                        isAddInvoiceNoteMutating > 0 ||
                        isAddInvoiceNoteDocMutating > 0
                    }
                >
                    <AddInvoiceNoteForm
                        invoiceId={invoiceId}
                        onFormSubmit={() => setDialogInView(null)}
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "view_all"}>
                <DialogContent
                    title="All internal notes"
                    handleCloseDialog={() => setDialogInView(null)}
                    showFooter={false}
                >
                    <InvoiceNotesList invoiceId={invoiceId} />
                </DialogContent>
            </Dialog>

            <div className={styles.wrapper}>
                {isLoading && <Loader wrapperClassName="h-full" />}
                {error && error?.response?.status !== 404 && (
                    <ListState
                        errorMsg={
                            error?.response?.data.message ||
                            `Cannot display internal notes`
                        }
                        stateHelperText="Try reloading this page to solve this issue"
                        isError
                        cardClass="h-full"
                        wrapperClass="h-full"
                        listHeader={listHeader}
                    />
                )}

                {data && isSuccess && data.data.length === 0 && (
                    <ListState
                        stateHelperText="Internal notes will be added here when added"
                        isError={false}
                        listHeader={listHeader}
                        cardClass="h-full"
                        emptyMessage="No internal notes"
                        wrapperClass="h-full"
                    />
                )}

                {isSuccess && data.data && data.data.length > 0 && (
                    <Card classNames="md:h-full">
                        {listHeader}

                        <div
                            className={cn(styles.cards, {
                                [styles.cards_small]: isNoShowNote,
                            })}
                            role="listbox"
                        >
                            {Object.keys(sortedInternalNotes).map(
                                (item, idx) => {
                                    return (
                                        <div key={item + String(idx)}>
                                            <div className={styles.card}>
                                                <li
                                                    className={styles.card_date}
                                                >
                                                    {item}
                                                </li>

                                                {sortedInternalNotes[item].map(
                                                    (note) => (
                                                        <div
                                                            key={
                                                                note.invoice_note_id
                                                            }
                                                            className="w-full flex flex-col gap-y-2"
                                                        >
                                                            <div className="flex justify-between gap-x-2 w-full">
                                                                <div className="flex gap-y-2 flex-col">
                                                                    <span
                                                                        className={
                                                                            styles.card_author
                                                                        }
                                                                    >
                                                                        {displayNameInRightFormat(
                                                                            {
                                                                                firstName:
                                                                                    note
                                                                                        .author
                                                                                        .first_name,
                                                                                lastName:
                                                                                    note
                                                                                        .author
                                                                                        .last_name,
                                                                            }
                                                                        )}
                                                                    </span>

                                                                    <span
                                                                        className={
                                                                            styles.card_content
                                                                        }
                                                                    >
                                                                        <ViewEditorText
                                                                            text={
                                                                                note.description
                                                                            }
                                                                        />
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center self-start">
                                                                    <ActionButton
                                                                        variant="can-hover"
                                                                        size="auto"
                                                                        className="h-7"
                                                                        onClick={() => {
                                                                            setDialogInView(
                                                                                "delete"
                                                                            );
                                                                            setSelectedInvoiceNoteId(
                                                                                note.invoice_note_id
                                                                            );
                                                                        }}
                                                                        actionTitle="Delete note"
                                                                    >
                                                                        <DeleteIcon />
                                                                    </ActionButton>
                                                                    <ActionButton
                                                                        variant="can-hover"
                                                                        size="auto"
                                                                        className="h-7"
                                                                        actionTitle="Edit note"
                                                                        onClick={() =>
                                                                            handleShowEditDialog(
                                                                                note
                                                                            )
                                                                        }
                                                                    >
                                                                        <EditIcon />
                                                                    </ActionButton>
                                                                </div>
                                                            </div>
                                                            {note.documents &&
                                                            note.documents
                                                                .length > 0 ? (
                                                                <div className="text-caption">
                                                                    <span className="font-semibold">
                                                                        Documents:
                                                                    </span>
                                                                    {note.documents.map(
                                                                        (
                                                                            doc,
                                                                            i,
                                                                            arr
                                                                        ) => (
                                                                            <ActionButton
                                                                                key={
                                                                                    doc.invoice_note_document_id
                                                                                }
                                                                                variant="can-hover"
                                                                                size="auto"
                                                                                className="h-7 !text-text text-caption underline"
                                                                                actionTitle="View"
                                                                                onClick={() => {
                                                                                    setDialogInView(
                                                                                        "view_doc"
                                                                                    );
                                                                                    setDocInView(
                                                                                        doc
                                                                                    );
                                                                                }}
                                                                            >
                                                                                <span>{`${displayNameInRightFormat(
                                                                                    {
                                                                                        firstName:
                                                                                            doc
                                                                                                .uploaded_by
                                                                                                .first_name,
                                                                                        lastName:
                                                                                            doc
                                                                                                .uploaded_by
                                                                                                .last_name,
                                                                                    },
                                                                                    false
                                                                                )}, ${formatDate(
                                                                                    doc.created_at
                                                                                )}${
                                                                                    i +
                                                                                        1 ===
                                                                                    arr.length
                                                                                        ? ""
                                                                                        : ","
                                                                                }`}</span>
                                                                            </ActionButton>
                                                                        )
                                                                    )}
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    );
                                }
                            )}
                        </div>
                    </Card>
                )}
            </div>
        </>
    );
}
